import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { ArrowLeftIcon, CheckIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { Progress } from "antd";
import { slugify } from "@/utils/text";
import {
  PageWrapper,
  BackLink,
  ViewContent,
  ViewTag,
  ViewTitle,
  MobileSubtitle,
} from "./styles";
import { EncryptedNote } from "./EncryptedNote";
import axiosInstance from "@/services/api";
import { API_URL } from "@/constants";
import { useProjectCreation } from "../../projects/hooks/useProjectCreation";
import { ComplianceFooter } from "../../projects/components/ComplianceFooter";
import { useAntTheme } from "hooks/useAntTheme/";
import { radius } from "@libertify/ui";

// ─── Types ────────────────────────────────────────────────────────────────────

type Phase = "idle" | "submitting" | "complete";

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const checkIn = keyframes`
  from { transform: scale(0.8); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const MobileLogo = styled.img`
  width: 50px;
  height: 50px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const InputCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: white;
  border-radius: ${radius.box}px;
  border: 1px solid #c9cfcf;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 140px;
  max-height: 40vh;
  overflow-y: auto;
  padding: 24px 24px 12px;
  border: none;
  outline: none;
  resize: none;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  color: #090e19;
  line-height: 1.6;
  background: transparent;

  &::placeholder {
    color: #9ca3af;
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 24px 24px;
`;

const StartButton = styled.button`
  height: 40px;
  padding: 0 24px;
  background: #14b8a6;
  border: none;
  border-radius: ${radius.pill}px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #0d9488;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusCard = styled.div`
  width: 100%;
  background: white;
  border-radius: ${radius.box}px;
  border: 1px solid #c9cfcf;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 40px;
  box-sizing: border-box;
`;

const ContentFade = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80%;
  animation: ${fadeIn} 0.28s ease both;
`;

const StatusLabel = styled.span`
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  margin-top: 10px;
`;

const CheckRing = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${radius.circle};
  border: 2.5px solid #14b8a6;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${checkIn} 0.4s ease both;
`;

// ─── Component ───────────────────────────────────────────────────────────────

export const ProjectInitPrompt: React.FC = () => {
  const navigate = useNavigate();
  const t = useTranslate();
  const { isMobile } = useAntTheme();
  const [prompt, setPrompt] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState(0);

  const { createProject } = useProjectCreation();

  const handleSubmit = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) return;

    setPhase("submitting");
    setProgress(10);

    try {
      const preview = trimmedPrompt.split(/\s+/).slice(0, 10).join(" ");
      const name = `prompt-video-${slugify(preview)}.pdf`;
      const project = await createProject(
        [name],
        "new_video",
        [{ tag_key: "prompt", tag_value: "prompt" }],
        "auto",
        true,
        null,
        undefined,
        { include_document_animations: false },
        "prompt"
      );
      setProgress(50);

      await axiosInstance.post(
        `${API_URL}/media/projects/${project.id}/documents/${encodeURIComponent(name)}`,
        {
          name,
          document_type: "Unknown",
          document_generation_definition: {
            generation_prompt: trimmedPrompt,
          },
        }
      );
      setProgress(100);
      setPhase("complete");

      setTimeout(() => {
        navigate(`/media/projects/${project.id}/slides-streaming`);
      }, 600);
    } catch (error) {
      console.error("Failed to create project from prompt:", error);
      setPhase("idle");
      setProgress(0);
    }
  };

  return (
    <>
      <PageWrapper>
        <ViewContent $isMobile={isMobile}>
          <BackLink
            type="button"
            onClick={() => navigate("/media/projects/create")}
            aria-label={t("pages.prompt.index.back")}
            $isMobile={isMobile}
          >
            <ArrowLeftIcon size={16} />
            {!isMobile && t("pages.prompt.index.back")}
          </BackLink>

          {isMobile ? (
            <>
              <MobileLogo src="/mini_logo.svg" alt="" />
              <ViewTitle $tight>{t("pages.prompt.index.title")}</ViewTitle>
              <MobileSubtitle>
                {t("pages.prompt.index.mobileSubtitle")}
              </MobileSubtitle>
            </>
          ) : (
            <>
              <ViewTag>{t("pages.prompt.index.tagline")}</ViewTag>
              <ViewTitle>
                {t("pages.prompt.index.title")}
                <br />
                {t("pages.prompt.index.titleSuffix")}
              </ViewTitle>
            </>
          )}

          {phase === "idle" && (
            <InputCard>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t(
                  isMobile
                    ? "pages.prompt.index.mobilePlaceholder"
                    : "pages.prompt.index.placeholder"
                )}
              />
              <CardFooter>
                <StartButton
                  type="button"
                  disabled={!prompt.trim()}
                  onClick={() => {
                    handleSubmit().catch(console.error);
                  }}
                >
                  {t("pages.prompt.index.startCreating")}
                </StartButton>
              </CardFooter>
            </InputCard>
          )}

          {phase === "submitting" && (
            <StatusCard>
              <ContentFade>
                <Progress
                  percent={Math.round(progress)}
                  strokeColor="#14B8A6"
                  trailColor="#CCFBF1"
                  strokeWidth={8}
                  showInfo={false}
                />
                <StatusLabel>{t("pages.prompt.index.submitting")}</StatusLabel>
              </ContentFade>
            </StatusCard>
          )}

          {phase === "complete" && (
            <StatusCard>
              <ContentFade style={{ alignItems: "center" }}>
                <CheckRing>
                  <CheckIcon size={24} color="#14B8A6" weight="bold" />
                </CheckRing>
              </ContentFade>
            </StatusCard>
          )}

          {!isMobile && <EncryptedNote />}
        </ViewContent>
      </PageWrapper>
      {!isMobile && <ComplianceFooter />}
    </>
  );
};
